import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { PillInfo } from "./types/gallus";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { FetchBaseQueryArgs } from "@reduxjs/toolkit/dist/query/fetchBaseQuery";

const gallusMenuApiBaseURL: FetchBaseQueryArgs = {
  baseUrl: "https://gallus-tools.gallusanalytics.com/api/Pills/",
};

function extractGuidFromUrl(url: string): string | null {
  // Decode URL in case it's encoded (e.g., %2F -> /)
  const decodedUrl = decodeURIComponent(url);

  // Match any of the patterns that include "pinboard/{GUID}"
  const match = decodedUrl.match(/pinboard\/([a-zA-Z0-9-]+)/);

  // Return the GUID if found, otherwise null
  return match ? "pinboard/" + match[1] : null;
}

const baseQuery = fetchBaseQuery(gallusMenuApiBaseURL);
const baseQueryWithErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  return result;
};

export const gallusMenuApi = createApi({
  reducerPath: "gallusMenuApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Pills"],
  endpoints: (builder) => ({
    GetAllPills: builder.query<PillInfo[], void>({
      query: () => {
        return {
          url: `GetAllPills`,
          method: "get",
        };
      },
    }),
    GetPillsByWorkspace: builder.query<PillInfo[], { workspace: string }>({
      query: (args) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("pathLevel", args.workspace);
        return {
          url: `GetAllPillsByPathLevel?${urlSearchParams.toString()}`,
          method: "get",
        };
      },
      providesTags: ["Pills"],
    }),
    AddPill: builder.mutation<
      boolean,
      { title: string; url: string; workspace: string }
    >({
      query: (args) => {
        const { title, url, workspace } = args;

        const formattedLink = extractGuidFromUrl(url);

        const bodyFormData = {
          title: title,
          link: formattedLink,
          pathLevel: workspace,
          styleTitle: "",
          description: "",
          styleDescription: "",
          styleLink: "",
          active: true,
        };

        return {
          url: ``,
          body: bodyFormData,
          method: "post",
        };
      },
      invalidatesTags: ["Pills"],
    }),
    UpdatePill: builder.mutation<
      boolean,
      { id: string; title: string; url: string; workspace: string }
    >({
      query: (args) => {
        const { id, title, url, workspace } = args;

        const formattedLink = extractGuidFromUrl(url);

        const bodyFormData = {
          id: id,
          title: title,
          link: formattedLink,
          description: "",
          pathLevel: workspace,
          styleDescription: "",
          styleLink: "",
          styleTitle: "",
          active: true,
        };

        return {
          url: ``,
          body: bodyFormData,
          method: "put",
        };
      },
      invalidatesTags: ["Pills"],
    }),
    DeletePill: builder.mutation<PillInfo[], { pillID: number }>({
      query: (args) => {
        const bodyFormData = {
          id: args.pillID.toString(),
          title: "",
          link: "",
          pathLevel: "",
          styleTitle: "",
          description: "",
          styleDescription: "",
          styleLink: "",
        };
        return {
          url: "",
          body: bodyFormData,
          method: "Delete",
        };
      },
      invalidatesTags: ["Pills"],
    }),
  }),
});

export const {
  useLazyGetAllPillsQuery,
  useLazyGetPillsByWorkspaceQuery,
  useAddPillMutation,
  useUpdatePillMutation,
  useDeletePillMutation,
} = gallusMenuApi;
